import { useEffect, useRef, useState } from "react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Upload, Check, Loader2, Locate, MapPin, Search } from "lucide-react";
import type { CmsField } from "./cmsFieldsConfig";
import { cn } from "@/lib/utils";
import MiniMap, { geocodeAddress } from "@/components/booking/MiniMap";
import { useToast } from "@/hooks/use-toast";

interface Props {
  field: CmsField;
  value: string;
  saving: boolean;
  uploading: boolean;
  /** Storage key (may differ from field.key when editing a non-PT locale). */
  storageKey?: string;
  /** Current PT-PT base value shown for context when editing a translation. */
  baseValue?: string;
  /** Companion values keyed by their full storage key. Used by address type. */
  companionValues?: Record<string, string>;
  onChange: (key: string, value: string) => void;
  onSave: (key: string) => void;
  onImageUpload: (key: string, file: File) => void;
  onRemoveImage?: (key: string) => void;
  /** Auto-save with debounce (ms). Set 0 to disable. Default 700. */
  autoSaveMs?: number;
}

const CmsFieldRenderer = ({
  field, value, saving, uploading,
  storageKey, baseValue, companionValues,
  onChange, onSave, onImageUpload, onRemoveImage,
  autoSaveMs = 700,
}: Props) => {
  const key = storageKey || field.key;
  const { toast } = useToast();
  const [justSaved, setJustSaved] = useState(false);
  const [geocoding, setGeocoding] = useState(false);
  const [gpsLoading, setGpsLoading] = useState(false);
  const timerRef = useRef<number | null>(null);
  const firstRun = useRef(true);

  // Debounced auto-save for text/textarea fields
  useEffect(() => {
    if (field.type !== "text" && field.type !== "textarea" && field.type !== "color" && field.type !== "address") return;
    if (autoSaveMs <= 0) return;
    if (firstRun.current) { firstRun.current = false; return; }
    if (timerRef.current) window.clearTimeout(timerRef.current);
    timerRef.current = window.setTimeout(() => {
      onSave(key);
      setJustSaved(true);
      window.setTimeout(() => setJustSaved(false), 1500);
    }, autoSaveMs);
    return () => { if (timerRef.current) window.clearTimeout(timerRef.current); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value, key]);

  const showBase = baseValue !== undefined && baseValue !== "" && baseValue !== value;

  const StatusIcon = () => (
    <span className="inline-flex items-center gap-1 text-xs text-muted-foreground min-w-[60px]">
      {saving ? (
        <><Loader2 size={12} className="animate-spin" /> A guardar…</>
      ) : justSaved ? (
        <><Check size={12} className="text-green-600" /> Guardado</>
      ) : null}
    </span>
  );

  // Show the current site default (only for the base pt-PT locale, when no value yet)
  const showDefault =
    !showBase &&
    !value &&
    !!field.defaultValue &&
    (field.type === "text" || field.type === "textarea");

  const placeholder = field.defaultValue || field.hint || "";

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between gap-2">
        <Label className="flex items-center gap-2 text-sm">
          <field.icon size={14} className="text-muted-foreground" /> {field.label}
        </Label>
        <StatusIcon />
      </div>
      {field.hint && (
        <p className="text-xs text-muted-foreground/70">{field.hint}</p>
      )}
      {showBase && (
        <div className="text-xs bg-muted/60 border border-border/60 rounded-lg px-3 py-2">
          <span className="font-semibold text-muted-foreground">Atual (PT-PT): </span>
          <span className="text-foreground/80 whitespace-pre-line">{baseValue}</span>
        </div>
      )}
      {showDefault && (
        <div className="text-xs bg-primary/5 border border-primary/15 rounded-lg px-3 py-2">
          <span className="font-semibold text-primary/80">Texto atual no site: </span>
          <span className="text-foreground/80 whitespace-pre-line">{field.defaultValue}</span>
        </div>
      )}

      {field.type === "text" && (
        <Input
          value={value}
          onChange={(e) => onChange(key, e.target.value)}
          placeholder={placeholder}
        />
      )}

      {field.type === "address" && (() => {
        const latKey = `${key}_lat`;
        const lngKey = `${key}_lng`;
        const lat = parseFloat(companionValues?.[latKey] || "");
        const lng = parseFloat(companionValues?.[lngKey] || "");
        const coords = isFinite(lat) && isFinite(lng) ? { lat, lng } : null;

        const saveCoords = (la: number, ln: number) => {
          onChange(latKey, String(la));
          onChange(lngKey, String(ln));
          // Persist immediately (bypass debounce)
          setTimeout(() => { onSave(latKey); onSave(lngKey); }, 50);
        };

        const doGeocode = async () => {
          if (!value.trim()) return;
          setGeocoding(true);
          const c = await geocodeAddress(value);
          setGeocoding(false);
          if (!c) { toast({ title: "Não foi possível localizar essa morada", variant: "destructive" }); return; }
          saveCoords(c.lat, c.lng);
          toast({ title: "Coordenadas atualizadas ✓" });
        };

        const doGps = () => {
          if (!navigator.geolocation) { toast({ title: "GPS indisponível", variant: "destructive" }); return; }
          setGpsLoading(true);
          navigator.geolocation.getCurrentPosition(
            async (pos) => {
              const { latitude, longitude } = pos.coords;
              try {
                const res = await fetch(`https://nominatim.openstreetmap.org/reverse?format=json&lat=${latitude}&lon=${longitude}&addressdetails=1`);
                const data = await res.json();
                const addr = data.display_name || `${latitude}, ${longitude}`;
                onChange(key, addr);
              } catch { /* keep previous */ }
              saveCoords(latitude, longitude);
              setGpsLoading(false);
              toast({ title: "Localização obtida ✓" });
            },
            () => { setGpsLoading(false); toast({ title: "Erro ao obter GPS", variant: "destructive" }); },
            { enableHighAccuracy: true, timeout: 10000 }
          );
        };

        return (
          <div className="space-y-2">
            <Input
              value={value}
              onChange={(e) => onChange(key, e.target.value)}
              placeholder={placeholder}
            />
            <div className="flex flex-wrap gap-2">
              <Button type="button" size="sm" variant="outline" onClick={doGeocode} disabled={geocoding || !value.trim()} className="gap-1">
                {geocoding ? <Loader2 size={14} className="animate-spin" /> : <Search size={14} />}
                Obter coordenadas
              </Button>
              <Button type="button" size="sm" variant="outline" onClick={doGps} disabled={gpsLoading} className="gap-1">
                {gpsLoading ? <Loader2 size={14} className="animate-spin" /> : <Locate size={14} />}
                Usar minha localização
              </Button>
              {coords && (
                <span className="inline-flex items-center gap-1 text-xs text-muted-foreground">
                  <MapPin size={12} /> {coords.lat.toFixed(5)}, {coords.lng.toFixed(5)}
                </span>
              )}
            </div>
            <MiniMap coords={coords} label={value} height={200} />
          </div>
        );
      })()}

      {field.type === "textarea" && (
        <Textarea
          value={value}
          onChange={(e) => onChange(key, e.target.value)}
          rows={Math.min(10, Math.max(4, Math.ceil((field.defaultValue?.length || 0) / 90)))}
          placeholder={placeholder}
        />
      )}

      {field.type === "image" && (
        <div className="space-y-3">
          {value && (
            <div className="relative inline-block">
              <img src={value} alt={field.label} className="h-32 w-auto rounded-xl border border-border object-cover" />
              <button
                type="button"
                onClick={() => onRemoveImage?.(key)}
                className="absolute -top-2 -right-2 w-6 h-6 rounded-full bg-destructive text-destructive-foreground flex items-center justify-center text-xs font-bold hover:opacity-80 transition-opacity"
                title="Remover imagem"
              >
                ✕
              </button>
            </div>
          )}
          <div className="flex items-center gap-2">
            <label className="cursor-pointer inline-flex items-center gap-2 rounded-lg border border-border px-4 py-2 text-sm hover:bg-muted transition-colors">
              <Upload size={14} />
              {uploading ? "A carregar..." : "Escolher imagem"}
              <input
                type="file"
                accept="image/*"
                className="hidden"
                onChange={(e) => {
                  const file = e.target.files?.[0];
                  if (file) onImageUpload(key, file);
                }}
              />
            </label>
          </div>
        </div>
      )}

      {field.type === "color" && (
        <div className="flex gap-2 items-center">
          <input
            type="color"
            value={value || "#8B2252"}
            onChange={(e) => onChange(key, e.target.value)}
            className="w-12 h-10 rounded-lg border border-border cursor-pointer"
          />
          <Input
            value={value}
            onChange={(e) => onChange(key, e.target.value)}
            placeholder="#8B2252"
            className="max-w-[140px]"
          />
        </div>
      )}
    </div>
  );
};

export default CmsFieldRenderer;
